import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { getStats } from '../utils/statsCounter';

const StatsContainer = styled.div`
  background-color: rgba(var(--card-background-rgb), 0.7);
  border-radius: var(--border-radius);
  margin-top: 1.5rem;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(var(--primary-color-rgb), 0.15);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, var(--primary-color), var(--secondary-color), var(--accent-color));
    border-radius: var(--border-radius) var(--border-radius) 0 0;
  }
`;

const StatsHeader = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(var(--primary-color-rgb), 0.1);
`;

const Title = styled.h3`
  color: var(--text-color);
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: var(--accent-color);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 0.75rem 0;
  
  &:not(:last-child) {
    border-right: 1px solid rgba(var(--primary-color-rgb), 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0;
  }
`;

const StatValue = styled(motion.div)`
  font-size: 1.35rem;
  font-weight: bold;
  background: linear-gradient(to right, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.2rem;
  position: relative;
`;

const UpdateFlash = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(var(--accent-color-rgb), 0.3);
  border-radius: 4px;
  z-index: -1;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: var(--text-color-secondary);
`;

const CounterStats = () => {
  const [stats, setStats] = useState({
    godsRolled: 0,
    buildsRolled: 0,
    totalRolls: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [changedStats, setChangedStats] = useState({
    godsRolled: false,
    buildsRolled: false,
    totalRolls: false
  });
  const prevStatsRef = useRef({
    godsRolled: 0,
    buildsRolled: 0,
    totalRolls: 0
  });
  
  // Separate state for animation lock to prevent glitchy animations
  const [animationLock, setAnimationLock] = useState(false);
  // Track fetch in progress to prevent race conditions
  const fetchInProgressRef = useRef(false);

  const fetchData = async () => {
    // Prevent concurrent fetches
    if (fetchInProgressRef.current) return;
    
    fetchInProgressRef.current = true;
    try {
      const currentStats = await getStats();
      
      // Only process if we're not in the middle of an animation
      if (!animationLock) {
        // Check if any stats have changed to trigger animation
        const newChangedStats = {
          godsRolled: prevStatsRef.current.godsRolled !== currentStats.godsRolled,
          buildsRolled: prevStatsRef.current.buildsRolled !== currentStats.buildsRolled,
          totalRolls: prevStatsRef.current.totalRolls !== currentStats.totalRolls
        };
        
        if (newChangedStats.godsRolled || newChangedStats.buildsRolled || newChangedStats.totalRolls) {
          // Lock animations during transition
          setAnimationLock(true);
          setChangedStats(newChangedStats);
          
          // Update the state with new values
          setStats(currentStats);
          
          // Store the current values for future comparison
          prevStatsRef.current = { ...currentStats };
          
          // Release animation lock after animation completes
          setTimeout(() => {
            setChangedStats({
              godsRolled: false,
              buildsRolled: false,
              totalRolls: false
            });
            
            // Release animation lock
            setAnimationLock(false);
          }, 1500); // Slightly longer than animation to ensure completion
        } else {
          // No change, just update the state silently
          setStats(currentStats);
        }
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to load community stats', error);
      setIsLoading(false);
    } finally {
      fetchInProgressRef.current = false;
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchData();

    // Polling interval - adjusted to 6 seconds to allow animations to complete
    const intervalId = setInterval(fetchData, 6000);
    
    return () => clearInterval(intervalId);
  }, []);

  return (
    <StatsContainer>
      <StatsHeader>
        <Title>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M4 11H2v3h2v-3zm5-4H7v7h2V7zm5-5v12h-2V2h2zm-2-1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1h-2zM6 7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7zm-5 4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-3z"/>
          </svg>
          Community Stats {isLoading && <LoadingDot>•</LoadingDot>}
        </Title>
      </StatsHeader>
      <StatsGrid>
        <StatCard>
          <StatValue>
            {stats.godsRolled.toLocaleString()}
            <AnimatePresence>
              {changedStats.godsRolled && (
                <UpdateFlash 
                  key="gods-flash"
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.2 }}
                />
              )}
            </AnimatePresence>
          </StatValue>
          <StatLabel>Gods Rolled</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>
            {stats.buildsRolled.toLocaleString()}
            <AnimatePresence>
              {changedStats.buildsRolled && (
                <UpdateFlash 
                  key="builds-flash"
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.2 }}
                />
              )}
            </AnimatePresence>
          </StatValue>
          <StatLabel>Builds Created</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>
            {stats.totalRolls.toLocaleString()}
            <AnimatePresence>
              {changedStats.totalRolls && (
                <UpdateFlash 
                  key="total-flash"
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.2 }}
                />
              )}
            </AnimatePresence>
          </StatValue>
          <StatLabel>Total Randomizations</StatLabel>
        </StatCard>
      </StatsGrid>
    </StatsContainer>
  );
};

// Add a loading indicator
const LoadingDot = styled.span`
  color: var(--accent-color);
  margin-left: 0.5rem;
  animation: pulse 1.5s infinite;
  
  @keyframes pulse {
    0% { opacity: 0.3; }
    50% { opacity: 1; }
    100% { opacity: 0.3; }
  }
`;

export default CounterStats; 